// src/services/mosqueService.ts
import axios from "axios";
import { API_BASE_URL } from "../config";
import { asErr } from "../utils/apiHelpers";

// ---- Types following your exact C# DTOs ----
export interface MosqueItemDto {
  id: number;                    // C# long
  name: string;
  regionName: string;
  mosqueClassification: string;
  cityName: string;
  centerName: string;
  districtName?: string;         // ✅ District name from API
  latitude: number;              // C# double
  longitude: number;             // C# double
  capacity: number | null;       // C# int?
  mosqueType: string;
  fullAddress: string;
  number: string;

  // ✅ NEW: unify API shapes (search returns hasWomenPrayerRoom="yes"/"no")
  // Keep both optional so we remain backward/forward compatible.
  hasWomenPrayerRoom?: string | boolean | null;
  hasWomenSection?: string | boolean | null;
}

export interface MosqueDetailDto {
  // Base properties (same as MosqueItemDto)
  id: number;
  name: string;
  regionName: string;
  cityName: string;
  centerName: string;
  districtName?: string;         // ✅ District name from API
  latitude: number;
  longitude: number;
  capacity: number | null;
  mosqueType: string;
  fullAddress: string;
  mosqueClassification: string;
  number: string;

  // Additional details
  phone?: string;
  email?: string;
  imamName?: string;

  // ✅ Widened slightly for symmetry (server may send "yes"/"no" or boolean)
  hasWomenSection?: boolean | string | null;

  description?: string;
  isActive: boolean;
  createdAt?: string;           // C# DateTime?
  updatedAt?: string;           // C# DateTime?

  // ✅ Returned by fn_get_mosque_details_by_id (V2)
  classificationId?: number | null;
  classificationName?: string | null;
  hasWomenPrayerRoom?: string | boolean | null; // "yes" | "no" | boolean | null
  totalCount?: number;                // usually 1 in detail
  pageCount?: number;                 // usually 1 in detail
}

export type MosqueListQuery = {
  regionId?: number;
  cityId?: number;
  centerId?: number;
  classificationId?: number;      // reserved (ignored for now)
  mosqueTypeId?: number;          // NEW reserved (ignored for now)
  hasWomenSection?: boolean;      // reserved (ignored for now)
  keyword?: string;               // API uses 'keyword' not 'searchQuery'
  // ✅ NEW: backend optional filters
  mosqueName?: string;
  mosqueNumber?: string;

  pageNo: number;
  pageSize: number;
};

export type MosqueListResponse = {
  pageNo: number;               // C# int
  pageSize: number;             // C# int
  totalCount: number;           // C# long (but JS number can handle it)
  pageCount: number;            // C# int
  items: MosqueItemDto[];       // C# IEnumerable<MosqueItemDto>
};

// ---- helpers ---------------------------------------------------------------
// ---- Map helpers (for mosque map points) ------------------------------------

// Clean params for map-points API
// We keep this SEPARATE from cleanedParams() used by listMosques
function cleanedMapParams(q: MosqueMapPointsQuery) {
  const out: Record<string, string | number | boolean> = {};

  // REQUIRED
  out.regionId = q.regionId;
  out.cityId = q.cityId;

  // OPTIONAL NUMBERS
  const putNum = (k: keyof MosqueMapPointsQuery) => {
    const v = q[k] as any;
    if (v === null || v === undefined) return;
    if (typeof v === "number" && !Number.isFinite(v)) return;
    out[k as string] = v;
  };

  // OPTIONAL STRINGS
  const putStr = (k: keyof MosqueMapPointsQuery) => {
    const v = q[k] as any;
    if (v === null || v === undefined) return;
    const s = String(v).trim();
    if (!s) return;
    out[k as string] = s;
  };

  putNum("centerId");
  putNum("classificationId");
  putStr("hasWomenPrayerRoom");

    // ✅ NEW: optional mosque filters
  putStr("mosqueName");
  putStr("mosqueNumber");

  
  // Coordinates must be BOTH present or BOTH absent
  if (
    typeof q.userLat === "number" &&
    typeof q.userLng === "number" &&
    Number.isFinite(q.userLat) &&
    Number.isFinite(q.userLng)
  ) {
    out.userLat = q.userLat;
    out.userLng = q.userLng;
  }

  // Defaults (server still enforces limits)
  out.radiusM = Number.isFinite(q.radiusM as any) && (q.radiusM as number) > 0
    ? (q.radiusM as number)
    : 5000;

  out.limit = Number.isFinite(q.limit as any) && (q.limit as number) > 0
    ? (q.limit as number)
    : 2000;

  return out;
}



// drop null/undefined/NaN params so we never send "?regionId=&cityId=null"
function cleanedParams(q: MosqueListQuery) {
  const out: Record<string, string | number | boolean> = {};

  const put = (k: keyof MosqueListQuery) => {
    const v = q[k];
    if (v === null || v === undefined) return;

    // ✅ keep numeric guard
    if (typeof v === "number" && !Number.isFinite(v)) return;

    // ✅ NEW: trim strings and skip empty ones
    if (typeof v === "string") {
      const s = v.trim();
      if (!s) return;
      out[k] = s;
      return;
    }

    out[k] = v as any;
  };

  put("regionId");
  put("cityId");
  put("centerId");
  put("classificationId");
  put("mosqueTypeId");
  put("hasWomenSection");
  put("keyword");

  // ✅ NEW
  put("mosqueName");
  put("mosqueNumber");

  // pageNo/pageSize are required; default if insane
  out.pageNo = Number.isFinite(q.pageNo) && q.pageNo > 0 ? q.pageNo : 1;
  out.pageSize = Number.isFinite(q.pageSize) && q.pageSize > 0 ? q.pageSize : 12;

  return out;
}


// Be explicit: if the server returns text/HTML we'll tell you immediately.
function parseJsonOrThrow(text: string) {
  const t = (text ?? "").toString().trim();
  if (!t.startsWith("{") && !t.startsWith("[")) {
    throw new Error(`Expected JSON but got: ${t.slice(0,160)}`);
  }
  try {
    return JSON.parse(t);
  } catch (e) {
    throw new Error(`Invalid JSON: ${t.slice(0,160)}`);
  }
}

// ---- axios instance ---------------------------------------------------------
const http = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? '' : API_BASE_URL,
  headers: { Accept: "application/json" },
  withCredentials: false,
  // Treat 3xx/4xx/5xx as errors so we hit our catch and show body snippet.
  validateStatus: (status) => status >= 200 && status < 300,
});

// ---- API --------------------------------------------------------------------
/** GET /api/mosques/map-points -> mosque map markers (PUBLIC) */
export async function listMosqueMapPoints(
  q: MosqueMapPointsQuery
): Promise<MosqueMapPointDto[]> {
  try {
    const { data: text } = await http.get<string>(
      "/api/mosques/map-points",
      {
        params: cleanedMapParams(q),
        responseType: "text",
        transformResponse: [(d) => d as string],
      }
    );

    const raw = parseJsonOrThrow(text);

    if (!Array.isArray(raw)) {
      throw new Error("Expected array from map-points API");
    }

    // Normalize response safely
    return raw.map((it: any) => ({
      id: it.id ?? it.Id,
      mosqueName: it.mosqueName ?? it.mosque_name ?? "",
      latitude: it.latitude ?? null,
      longitude: it.longitude ?? null,
      capacity: it.capacity ?? null,
      hasWomenPrayerRoom:
        it.hasWomenPrayerRoom ?? it.has_women_prayer_room ?? null,
      mosqueClassification:
        it.mosqueClassification ?? it.mosque_classification ?? "",
      distanceM: it.distanceM ?? it.distance_m ?? null,
    })) as MosqueMapPointDto[];
  } catch (err) {
    throw asErr("listMosqueMapPoints failed", err);
  }
}


export async function listMosques(q: MosqueListQuery): Promise<MosqueListResponse> {
  try {
    // Ask backend as TEXT. We'll validate JSON ourselves.
    const { data: text } = await http.get<string>("/api/mosques", {
      params: cleanedParams(q),
      responseType: "text",
      // prevent axios from attempting to auto-parse JSON
      transformResponse: [(d) => d as string],
    });

    const raw = parseJsonOrThrow(text);

    // Shape check
    if (!raw || !Array.isArray(raw.items) ||
        typeof raw.totalCount !== "number" ||
        typeof raw.pageCount !== "number") {
      throw new Error("Unexpected payload shape from API");
    }

    // ✅ Normalize item shape so UI can always read `hasWomenPrayerRoom`
    const items: MosqueItemDto[] = raw.items.map((it: any) => {
      const hasWomen =
        it.hasWomenPrayerRoom !== undefined && it.hasWomenPrayerRoom !== null
          ? it.hasWomenPrayerRoom
          : it.hasWomenSection;

      return {
        ...it,
        hasWomenPrayerRoom: hasWomen, // unify on this property
      } as MosqueItemDto;
    });

    return {
      pageNo: raw.pageNo,
      pageSize: raw.pageSize,
      totalCount: raw.totalCount,
      pageCount: raw.pageCount,
      items,
    };
  } catch (err) {
    throw asErr("listMosques failed", err);
  }
}

/** GET /api/mosques/:id -> mosque detail (PUBLIC) */
export async function getMosqueById(mosqueId: number): Promise<MosqueDetailDto> {
  try {
    const { data: text } = await http.get<string>(`/api/mosques/${mosqueId}`, {
      responseType: "text",
      transformResponse: [(d) => d as string],
    });

    const data = parseJsonOrThrow(text);

    if (!data || typeof data.id !== "number" || !data.name) {
      throw new Error("Unexpected mosque detail payload shape");
    }

    // (Optional) Could normalize detail too, but DTO already includes both fields.
    return data as MosqueDetailDto;
  } catch (err) {
    throw asErr("getMosqueById failed", err);
  }
}

// ---- Additional mosque-specific APIs ----

/** GET /api/mosques/classifications -> mosque classification types */
export async function getMosqueClassifications() {
  try {
    const { data: text } = await http.get<string>("/api/mosques/classifications", {
      responseType: "text",
      transformResponse: [(d) => d as string],
    });

    const data = parseJsonOrThrow(text);

    if (!Array.isArray(data)) {
      throw new Error("Expected array of classifications");
    }

    return data as Array<{ id: number; name: string }>;
  } catch (err) {
    throw asErr("getMosqueClassifications failed", err);
  }
}

export const mosqueTypeColors: Record<string, string> = {
  "جامع": "bg-sa-700 text-white",
  "فروض": "bg-sa-500 text-white",
  "العيد": "bg-sa-300 text-white",
};



// ---- Map points (Google Maps / Cluster) ------------------------------------

export type MosqueMapPointsQuery = {
  regionId: number;                 // required
  cityId: number;                   // required
  centerId?: number;
  classificationId?: number;
  hasWomenPrayerRoom?: string;      // "yes" | "no" | "true" | "false" | null
  // ✅ NEW: optional text filters
  mosqueName?: string;
  mosqueNumber?: string;

  userLat?: number;                // optional
  userLng?: number;                // optional
  radiusM?: number;                // optional, default backend
  limit?: number;                  // optional, default backend
};

export type MosqueMapPointDto = {
  id: number;
  mosqueName: string;
  latitude: number | null;
  longitude: number | null;
  capacity: number | null;
  hasWomenPrayerRoom?: string | boolean | null;
  mosqueClassification: string;
  distanceM?: number | null;
};
